import React, { useEffect, useMemo, useState } from "react";
import { useMatch } from "react-router-dom";

import styled from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Alert from "components/atoms/alert/Alert";
import { useLoading } from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";

import { BodyWithBreadcrumb, Breadcrumb, BreadcrumbTitle } from "components/molecules/Breadcrumb";
import PlanningStepSection from "components/organisms/report/PlanningStepSection";
import RequestStepSection from "components/organisms/report/RequestStepSection";
import ResultStepSection from "components/organisms/report/ResultStepSection";
import {
    INITIAL_REQUIREMENT_FIELDS,
    SOURCE_LABEL_MAP,
    STEP_ORDER,
    buildDefaultInitialSelections,
    resolveInitialRequirements,
} from "components/organisms/report/constants";
import LoginUserLayout from "components/templates/LoginUserLayout";

import { useUser } from "hooks/queries/useUser";
import { useSearchAgentWebSocket } from "hooks/useSearchAgentWebSocket";

import { isPaidPlanUser } from "utils/user";

const PageWrap = styled.div`
    width: 100%;
    max-width: 1240px;
    margin: 22px auto 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
`;

const StepperWrap = styled.div`
    display: flex;
    gap: 16px;
    padding: 0 2px;
`;

const StepItem = styled.div`
    position: relative;
    flex: 1;
    padding: 0 0 12px;

    &::before {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 3px;
        border-radius: 999px;
        background: ${(props) => {
            if (props.$state === "current") return "var(--color-Key)";
            if (props.$state === "done") return "var(--color-StepDone)";
            return "var(--color-Outline)";
        }};
    }
`;

const StepItemTitle = styled(Text).attrs({ variant: "body-md-500" })`
    color: ${(props) => (props.$state === "upcoming" ? "var(--color-Grey1)" : "var(--color-SubBlack)")};
`;

const StepItemText = styled(Text).attrs({ variant: "body-sm-400" })`
    color: var(--color-Grey1);
`;

const getStepVisualState = (activeStepIndex, targetIndex) => {
    if (activeStepIndex > targetIndex) return "done";
    if (activeStepIndex === targetIndex) return "current";
    return "upcoming";
};

function ReportPage() {
    const match = useMatch("/:space_uuid/*");
    const spaceUuid = match?.params?.space_uuid ?? "";
    const [queryInput, setQueryInput] = useState("");
    const [initialSelections, setInitialSelections] = useState(buildDefaultInitialSelections);
    const [initialCustomInputs, setInitialCustomInputs] = useState({});
    const { setLoading } = useLoading();
    const userQuery = useUser();

    const {
        ready,
        step,
        phase,
        error,
        planningState,
        resultState,
        submitRequest,
        confirmPlanning,
        saveNote,
        goBackStep,
        cancelSession,
    } = useSearchAgentWebSocket({ connectionDependencies: [true] });

    const hasInvalidInitialRequirementCustomInput = useMemo(() => {
        return INITIAL_REQUIREMENT_FIELDS.some((field) => {
            if (initialSelections[field.category] !== "other") return false;
            return !(initialCustomInputs[field.category] || "").trim();
        });
    }, [initialSelections, initialCustomInputs]);

    const canSubmitRequest = ready && queryInput.trim() && !hasInvalidInitialRequirementCustomInput;

    const activeStepIndex = Math.max(STEP_ORDER.indexOf(step), 0);
    const requestStepState = getStepVisualState(activeStepIndex, 0);
    const planningStepState = getStepVisualState(activeStepIndex, 1);
    const resultStepState = getStepVisualState(activeStepIndex, 2);

    const currentProgressMessage = (resultState.currentStageMessage || "").trim();

    const hasReportContent = useMemo(() => !!(resultState.report || "").trim(), [resultState.report]);

    useEffect(() => {
        if (step === "result" && resultState.streaming && !hasReportContent) {
            setLoading(true, "리서치를 진행 중입니다.", currentProgressMessage);
            return;
        }
        setLoading(false);
    }, [currentProgressMessage, hasReportContent, resultState.streaming, setLoading, step]);

    useEffect(() => {
        return () => {
            setLoading(false);
        };
    }, [setLoading]);

    const handleSubmitRequest = () => {
        const initialRequirements = resolveInitialRequirements(initialSelections, initialCustomInputs);
        if (!initialRequirements) return;
        submitRequest(queryInput, initialRequirements);
    };

    const handleConfirmPlanning = (planText) => {
        if (!planText) return;
        if (userQuery.isLoading) {
            Alert("info", "유료 기능 알림", "요금제 정보를 확인 중입니다. 잠시 후 다시 시도해주세요.");
            return;
        }
        if (!isPaidPlanUser(userQuery.data)) {
            Alert("info", "유료 기능 알림", "동향분석은 유료 기능입니다.");
            return;
        }
        confirmPlanning(planText);
    };

    const handleOpenNote = () => {
        if (!spaceUuid || !resultState.noteNo) return;
        window.open(`/${spaceUuid}/note/${resultState.noteNo}`, "_blank", "noopener noreferrer");
    };

    const handleSaveNote = () => {
        if (resultState.isSaving || resultState.isPersisted) return;
        saveNote();
    };

    //region: 마크업 시작
    return (
        <LoginUserLayout>
            <HtmlHead title={"동향분석"} />
            <Breadcrumb>
                <BreadcrumbTitle>동향분석</BreadcrumbTitle>
            </Breadcrumb>

            <BodyWithBreadcrumb>
                <PageWrap>
                    <StepperWrap>
                        <StepItem $state={requestStepState}>
                            <StepItemTitle $state={requestStepState}>1. 요청</StepItemTitle>
                            <StepItemText>연구 주제와 기본 조건 입력</StepItemText>
                        </StepItem>
                        <StepItem $state={planningStepState}>
                            <StepItemTitle $state={planningStepState}>2. 계획</StepItemTitle>
                            <StepItemText>연구 계획 확인 및 수정</StepItemText>
                        </StepItem>
                        <StepItem $state={resultStepState}>
                            <StepItemTitle $state={resultStepState}>3. 결과</StepItemTitle>
                            <StepItemText>연구 결과 생성 및 검토</StepItemText>
                        </StepItem>
                    </StepperWrap>

                    {step === "request" && (
                        <RequestStepSection
                            ready={ready}
                            queryInput={queryInput}
                            onQueryInputChange={setQueryInput}
                            initialFields={INITIAL_REQUIREMENT_FIELDS}
                            initialSelections={initialSelections}
                            initialCustomInputs={initialCustomInputs}
                            onSelectInitialOption={(category, optionId) =>
                                setInitialSelections((prev) => ({
                                    ...prev,
                                    [category]: optionId,
                                }))
                            }
                            onChangeInitialCustomInput={(category, value) =>
                                setInitialCustomInputs((prev) => ({
                                    ...prev,
                                    [category]: value,
                                }))
                            }
                            onSubmitRequest={handleSubmitRequest}
                            canSubmitRequest={canSubmitRequest}
                        />
                    )}

                    {step === "planning" && (
                        <PlanningStepSection
                            planningState={planningState}
                            onConfirmPlanning={handleConfirmPlanning}
                            onGoBackRequest={() => goBackStep("request")}
                        />
                    )}

                    {step === "result" && (
                        <ResultStepSection
                            phase={phase}
                            error={error}
                            resultState={resultState}
                            sourceLabelMap={SOURCE_LABEL_MAP}
                            onGoBackPlanning={() => goBackStep("planning")}
                            onCancelSession={cancelSession}
                            onOpenNote={handleOpenNote}
                            onSaveNote={handleSaveNote}
                        />
                    )}
                </PageWrap>
            </BodyWithBreadcrumb>
        </LoginUserLayout>
    );
}

export default ReportPage;
